import {
  CarePlanGoalResponse,
  CarePlanProblemResponse,
  CarePlanQolGoalResponse,
  CarePlanResponse,
} from '~/lib/types/carePlan/carePlans';
import { CarePlanGoalCard } from '~/components/maud/patients/profile/tabs/careplan/CarePlanGoalCard';
import { CarePlanQolGoalCard } from '~/components/maud/patients/profile/tabs/careplan/CarePlanQolGoalCard';
import { useMemo, useState } from 'react';
import { RemoveProblemConfirmationModal } from '~/components/maud/patients/profile/tabs/careplan/RemoveProblemConfirmationModal';
import EditQoLGoalModal from './EditQoLGoalModal';
import { RemoveQolGoalConfirmationModal } from '~/components/maud/patients/profile/tabs/careplan/RemoveQolGoalConfirmationModal';
import { EditGoalModal } from './EditGoalModal';
import { ActiveCarePlanTreatmentStagesSection } from './ActiveCarePlanTreatmentStagesSection';
import { EditProblemModal } from '~/components/maud/patients/profile/tabs/careplan/EditProblemModal';
import { ActionBar, FAIconName } from '@equip.health/ui';
import { ActiveCarePlanTreatmentGoalsSection } from './ActiveCarePlanTreatmentGoalsSection';
import { ActiveCarePlanCoOccurringSection } from './ActiveCarePlanCoOccurringSection';
import { ActiveCarePlanQoLGoalsSection } from './ActiveCarePlanQoLGoalsSection';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ProblemTypeCode } from '~/lib/types/carePlan/ProblemTypeCode';

type Props = {
  carePlan: CarePlanResponse;
  hasModalityChanged: boolean;
  treatmentEpisodeId: string;
};
export const ActiveCarePlan = ({
  carePlan,
  hasModalityChanged,
  treatmentEpisodeId,
}: Props) => {
  const { carePlanM4MedPsychProblems } = useFlags<LDFlags>();
  const [editingGoal, setEditingGoal] = useState<CarePlanGoalResponse | null>(
    null,
  );
  const [editingQoLGoal, setEditingQoLGoal] =
    useState<CarePlanQolGoalResponse | null>(null);

  const [editQoLGoalFocusDescription, setEditQoLGoalFocusDescription] =
    useState<boolean>(false);

  const handleAddQoLGoalDescriptionClick = (
    qolGoal: CarePlanQolGoalResponse,
  ) => {
    setEditingQoLGoal(qolGoal);
    setEditQoLGoalFocusDescription(true);
  };

  const handleCloseEditQoLGoal = () => {
    setEditingQoLGoal(null);
    setEditQoLGoalFocusDescription(false);
  };

  const [editingProblem, setEditingProblem] =
    useState<CarePlanProblemResponse | null>(null);
  const [removeProblemModalId, setRemoveProblemModalId] = useState<string>('');
  const [removeQolGoalModalId, setRemoveQolGoalModalId] = useState<string>('');

  const sortedProblems = useMemo(
    () =>
      carePlan.problems.toSorted(
        (problem1, problem2) =>
          problem1.pathwayProblem.sortOrder - problem2.pathwayProblem.sortOrder,
      ),
    [carePlan.problems],
  );

  const { edProblems, coOccurringProblems } = useMemo(() => {
    const edProblems: CarePlanProblemResponse[] = [];
    const coOccurringProblems: CarePlanProblemResponse[] = [];

    sortedProblems.forEach((problem) => {
      if (
        !problem.problemType ||
        problem.problemType === ProblemTypeCode.EATING_DISORDER
      ) {
        edProblems.push(problem);
      } else {
        coOccurringProblems.push(problem);
      }
    });

    return {
      edProblems,
      coOccurringProblems,
    };
  }, [sortedProblems]);

  const sortedQolGoals = useMemo(
    () =>
      carePlan.qolGoals.toSorted((goal1, goal2) => {
        return (
          new Date(goal1.createdAt).getTime() -
          new Date(goal2.createdAt).getTime()
        );
      }),
    [carePlan.qolGoals],
  );

  return (
    <>
      {!carePlan.treatmentEpisodeModality && (
        <ActionBar
          id="active-care-plan-no-interventions-until-modality"
          text="Core ED interventions won't appear on the care plan until treatment modality is selected"
          variant="info"
          barIcon={FAIconName.infoCircleRegular}
        />
      )}
      {carePlanM4MedPsychProblems ? (
        <>
          <ActiveCarePlanTreatmentGoalsSection
            problems={edProblems}
            treatmentEpisodeId={treatmentEpisodeId}
            carePlan={carePlan}
            onEditProblemClick={setEditingProblem}
            onEditGoalClick={setEditingGoal}
            onRemoveGoalClick={setRemoveProblemModalId}
          />
          <ActiveCarePlanCoOccurringSection
            problems={coOccurringProblems}
            treatmentEpisodeId={treatmentEpisodeId}
            carePlan={carePlan}
            onEditProblemClick={setEditingProblem}
            onEditGoalClick={setEditingGoal}
            onRemoveGoalClick={setRemoveProblemModalId}
          />
          <ActiveCarePlanQoLGoalsSection
            qolGoals={sortedQolGoals}
            treatmentEpisodeId={treatmentEpisodeId}
            carePlanId={carePlan.id}
            onAddDescriptionClick={handleAddQoLGoalDescriptionClick}
            onEditQoLGoalClick={setEditingQoLGoal}
            onRemoveQoLGoalClick={setRemoveQolGoalModalId}
          />
        </>
      ) : (
        <>
          {sortedProblems.map((problem) => (
            <CarePlanGoalCard
              carePlanId={carePlan.id}
              key={`${carePlan.id}__${problem.pathwayProblem.code}`}
              onEditClick={setEditingGoal}
              onEditProblemClick={setEditingProblem}
              onRemoveClick={setRemoveProblemModalId}
              problem={problem}
              treatmentEpisodeId={treatmentEpisodeId}
            />
          ))}
          {sortedQolGoals.map((qolGoal) => (
            <CarePlanQolGoalCard
              key={`${carePlan.id}__${qolGoal.title}_${qolGoal.description}`}
              onEditClick={setEditingQoLGoal}
              onRemoveClick={setRemoveQolGoalModalId}
              qolGoal={qolGoal}
              treatmentEpisodeId={treatmentEpisodeId}
            />
          ))}
        </>
      )}
      <ActiveCarePlanTreatmentStagesSection
        carePlan={carePlan}
        hasModalityChanged={hasModalityChanged}
        treatmentEpisodeId={treatmentEpisodeId}
      />
      {!!editingProblem && (
        <EditProblemModal
          onClose={() => setEditingProblem(null)}
          problem={editingProblem}
          treatmentEpisodeId={treatmentEpisodeId}
        />
      )}
      {!!removeProblemModalId && (
        <RemoveProblemConfirmationModal
          onClose={() => setRemoveProblemModalId('')}
          problemId={removeProblemModalId}
          treatmentEpisodeId={treatmentEpisodeId}
        />
      )}
      {!!editingGoal && (
        <EditGoalModal
          goal={editingGoal}
          onClose={() => setEditingGoal(null)}
          treatmentEpisodeId={treatmentEpisodeId}
        />
      )}
      {!!editingQoLGoal && (
        <EditQoLGoalModal
          isInitialFocusDescription={editQoLGoalFocusDescription}
          onClose={handleCloseEditQoLGoal}
          qolGoal={editingQoLGoal}
          treatmentEpisodeId={treatmentEpisodeId}
        />
      )}
      {!!removeQolGoalModalId && (
        <RemoveQolGoalConfirmationModal
          goalId={removeQolGoalModalId}
          onClose={() => setRemoveQolGoalModalId('')}
          treatmentEpisodeId={treatmentEpisodeId}
        />
      )}
    </>
  );
};
